import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, filter, map, mergeMap, switchMap } from 'rxjs/operators';
import { combineLatest, Subscription } from 'rxjs';
import { OAuthService } from 'angular-oauth2-oidc';

import { StorageService } from '@core/services/storage.service';
import { ClientAccountService } from '@shared/services/client-account.service';
import { UsersService } from '@shared/services/users.service';
import { ApplicationUserConverter } from '@shared/converters/application-user.converter';
import { CustomerConverter } from '@shared/converters/customer.converter';
import { AccountConverter } from '@shared/converters/account.converter';
import { SettingService } from '@shared/services/setting.service';
import { SettingModel } from '@shared/models/setting.model';
import { FeaturesService } from '@shared/services/features.service';
import { FeaturesResearchModel } from '@shared/models/features-research.model';
import { FeaturesResearchFilterModel } from '@shared/models/features-research-filter.model';
import { EventBusService } from '@core/services/event-bus.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnDestroy {

  pageTitle: string;
  client: string;
  account: string;
  routerSubscription: Subscription;
  isFlowActive: boolean;
  isWarpFlow: boolean;

  constructor(
    public oAuthService: OAuthService,
    translate: TranslateService,
    public activatedRoute: ActivatedRoute,
    public router: Router,
    public storageService: StorageService,
    public clientAccountService: ClientAccountService,
    public usersService: UsersService,
    private applicationUserConverter: ApplicationUserConverter,
    private customerConverter: CustomerConverter,
    private accountConverter: AccountConverter,
    private settingService: SettingService,
    public featuresService: FeaturesService,
    private eventBusService: EventBusService,
  ) {
    const availableLangs = ['it', 'en', 'es'];
    translate.addLangs(availableLangs);

    // this language will be used as a fallback when a translation isn't found in the current language
    translate.setDefaultLang('it');
    const lang = window.navigator.language.split("-")[0] || 'it';
    if (availableLangs.includes(lang)) {
      translate.use(lang);
    }

    // // prende la lingua se settata in db sennò default ita
    // this.settingService.loadConfiguration().then(sett => {
    //   if (sett.global?.language) {
    //     translate.use(sett.global?.language);
    //   } else {
    //     translate.use('it');
    //   }
    // });

    this.settingService.loadSetting().subscribe((sett: SettingModel) => {
      if (sett.global?.language) {
        translate.use(sett.global?.language);
      } else {
        translate.use('it');
      }
    });

    this.settingService.warpProcess.subscribe((value => {
      this.isWarpFlow = value;
    }));

    // manage page title header
    this.routerSubscription = this.router.events
      .pipe(
        // filter((event) => event instanceof NavigationEnd),
        map((ev) => {
          return this.activatedRoute;
        }),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        mergeMap((route) => route.data))
      .subscribe((data) => {
        // console.log('page ' + data['title']);
        this.pageTitle = data['title'];
      });

    if (!this.usersService.isCallCenterUser()) {
      //gestione customer e account
      this.manageCustomerAccount();
    }

    // gestione profilazione(features)
    this.manageFeatures();
    //gestione user
    this.manageUser();
  }

  ngOnDestroy(): void {
    this.routerSubscription.unsubscribe();
    this.clientAccountService.clearUnit(true);
    this.usersService.clearUser();
    this.storageService.deletePreference('features');
    localStorage.clear();
    sessionStorage.clear();
  }

  isAuthPages() {
    return this.router.url.includes('login');
  }

  manageFeatures() {
    combineLatest([
      this.clientAccountService.onAccountChoice,
      this.clientAccountService.onCustomerChoice,
    ])
      .pipe(
        debounceTime(150),
        map(([hasAccount, hasCustomer]) => hasAccount || hasCustomer),
        filter((hasAccountOrCustomer) => hasAccountOrCustomer),
        map(
          () =>
            new FeaturesResearchFilterModel(
              this.clientAccountService.getAccount()?.accountCode,
              this.clientAccountService.getClient()?.customerCode
            )
        ),
        switchMap((featuresFilter) =>
          this.featuresService.getFeatures(
            new FeaturesResearchModel(featuresFilter)
          )
        ),
      )
      .subscribe();
  }

  manageUser() {
    //gestione user
    if (this.storageService.getPreference('user')) {
      try {
        let userStorage = this.storageService.getPreference('user');
        this.usersService.setUser(this.applicationUserConverter.convertUserFormStorage(userStorage));
      } catch (e) {
        console.log(e);
        this.usersService.changeUser();
      }
    }
  }

  manageCustomerAccount() {
    if (this.storageService.getPreference('customer')) {
      try {
        let customerStorage = this.storageService.getPreference('customer');
        this.clientAccountService.setCustomer(this.customerConverter.convertCustomerFormStorage(customerStorage));
      } catch (e) {
        console.log(e);
        this.clientAccountService.changeClienteAccount();
      }
    }
    if (this.storageService.getPreference('account')) {
      try {
        let accountStorage = this.storageService.getPreference('account');
        this.clientAccountService.setAccount(this.accountConverter.convertAccountFormStorage(accountStorage));
      } catch (e) {
        console.log(e);
        this.clientAccountService.changeClienteAccount();
      }
    }
  }
}
